import {Box, Snackbar} from '@mui/material';
import {selectUpgradeRequired} from '@smartmixin/core';
import {messages} from '@smartmixin/i18n';
import React from 'react';
import {useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import {Banner} from '../../components/ui';
import {webappBuild} from '../../lib/appInfo';
import {captureWarningMessage} from '../../lib/SentryUtils';
import {elevation, radius} from '../../theme/tokens';

/**
 * The build that Reload was pressed in, for this tab (sessionStorage). If the
 * page comes back as the SAME build and the API still reports a newer
 * version, reloading cannot help: the backend's API_VERSION was bumped before
 * a webapp supporting it reached this channel. The banner then stays away
 * for this tab instead of asking for a reload that loops (as
 * lib/chunkReload.ts guards its own reload), and Sentry hears about it once.
 */
export const RELOAD_BUILD_KEY = 'sm:apiUpgradeReloadBuild';

const reloadPage = () => {
  try {
    sessionStorage.setItem(RELOAD_BUILD_KEY, webappBuild());
  } catch {
    // sessionStorage unavailable (private mode): reload anyway.
  }
  window.location.reload();
};

const reloadAlreadyTried = (): boolean => {
  try {
    return sessionStorage.getItem(RELOAD_BUILD_KEY) === webappBuild();
  } catch {
    return false;
  }
};

let staleBundleReported = false;

/**
 * The banner itself, mounted only while an update is required. Its message
 * arrives a frame after the Banner (role=status) mounts: screen readers watch
 * a region for changes, so one that appears together with its text is often
 * not read. Same technique as `components/LiveRegion`, applied to the
 * Banner's own region.
 */
const ReloadBanner = () => {
  const {formatMessage} = useIntl();
  const [ready, setReady] = React.useState(false);
  React.useEffect(() => {
    const frame = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    // Open for as long as it is mounted: no timer and no onClose, so neither
    // a click elsewhere nor Escape dismisses it. Above the page, below the
    // phone's and the signed-out top bar (--shell-top, set by AppContainer),
    // away from the alert snackbar at the bottom right. Under the app bar's
    // layer, not the snackbar's: dialogs, menus, popovers and the nav drawer
    // cover it rather than the other way round.
    <Snackbar
      open
      anchorOrigin={{vertical: 'top', horizontal: 'center'}}
      sx={{
        zIndex: (theme) => theme.zIndex.appBar - 1,
        top: {
          xs: 'calc(var(--shell-top, 0px) + 8px)',
          sm: 'calc(var(--shell-top, 0px) + 24px)',
        },
      }}>
      <Box
        data-testid="update-required-banner"
        sx={(theme) => ({
          width: {xs: '100%', sm: 'auto'},
          maxWidth: {sm: 640},
          borderRadius: `${radius.sm}px`,
          // A floating surface, as the alert snackbar.
          boxShadow: elevation.floating[theme.palette.mode],
        })}>
        <Banner
          severity="info"
          action={{
            label: formatMessage(messages.appUpdate.reload),
            onClick: reloadPage,
          }}>
          {ready ? formatMessage(messages.appUpdate.webUpdateAvailable) : null}
        </Banner>
      </Box>
    </Snackbar>
  );
};

/**
 * Tells a stale tab to reload (backend #290). Every API response carries
 * `X-Api-Version`; when it is newer than this bundle's
 * `SUPPORTED_API_VERSION`, core's `useApiClient` sets
 * `uiInteraction.upgradeRequired`. On the web, updating is reloading the
 * page: the new bundle is already deployed. Mobile blocks on the same flag
 * (UpgradeRequiredScreen); the web keeps working and asks.
 *
 * Persistent and not dismissible: the flag is never cleared in this tab
 * (`uiInteraction` is not persisted, so a reload starts it at false), and
 * every later response with the newer version would raise it again anyway.
 * Polite (role=status, an info Banner): the page may keep working, and the
 * user decides when to reload.
 *
 * Ordering: the backend's API_VERSION must not move past this bundle's
 * SUPPORTED_API_VERSION before a webapp supporting it is live; otherwise
 * Reload loads the same build. The guard above keeps that from looping.
 */
const UpdateRequiredBanner = () => {
  const required = useSelector(selectUpgradeRequired);
  const futile = required && reloadAlreadyTried();
  React.useEffect(() => {
    if (futile && !staleBundleReported) {
      staleBundleReported = true;
      captureWarningMessage(
        'Webapp bundle older than the API: reload did not help',
        {webapp_build: webappBuild()},
      );
    }
  }, [futile]);
  return required && !futile ? <ReloadBanner /> : null;
};

export default UpdateRequiredBanner;
